import {FormContainer, Text} from '@tempo/core';
import {FormControlField} from '@app/components/Form';
import {Formik} from 'formik';
import {getStaticTranslations} from '@app/modules/translations';
import {getTemplate} from '@app/modules/server/template';
import {GiftFloatingCards} from '@app/components/Gift/FloatingCards';
import {InAppFormLayout} from '@app/components/Layouts/pages/InAppFormLayout';
import {useGiftController} from '@app/hooks/controllers/useGiftController';
import {useTranslation} from '@app/hooks/useTranslation';
import type {DzrGetStaticProps, DzrPage} from '@app/types/next';
import {FlippingCard, RectoCard, VersoCard} from '@app/components/FlippingCard';
import {CheckCircleFilledIcon} from '@tempo/icons';
import {Form} from '@components/Form/Form';
import {useLocation} from '@app/hooks/useLocation';
import {useUserData} from '@app/hooks/controllers/useUserData';
import {Button} from '@app/components/BtnWrapper';

export const GiftPage: DzrPage = () => {
	const t = useTranslation();
	const {getDeezerUrl} = useLocation();
	const {user} = useUserData();
	const {isLoading, redeemCode, step, prefilledCode, isRouterReady} =
		useGiftController();

	if (!isRouterReady) {
		return null;
	}

	return (
		<InAppFormLayout title={t('gift.title_giftcard')}>
			<FlippingCard isFlipped={step === 'success'} maxWidth="container.sm">
				<RectoCard>
					<Text>{t('gift.description_giftform')}</Text>
					<Formik
						onSubmit={(values) => redeemCode(values.code)}
						validateOnChange={false}
						validateOnBlur={false}
						initialValues={{code: (prefilledCode as string) ?? ''}}
					>
						<Form>
							<FormContainer alignItems="center">
								<FormControlField
									label={t('gift.insertcode')}
									id="code"
									name="code"
									type="code"
									data-testid="code-field"
								/>
								<Button
									isLoading={isLoading}
									data-testid="validate-button"
									type="submit"
								>
									<span>{t('common.action_submit')}</span>
								</Button>
							</FormContainer>
						</Form>
					</Formik>
				</RectoCard>
				<VersoCard spacing={0} justifyContent="space-evenly">
					<Text>
						{t('gift.yourofferisactivated', {
							sprintf: [user?.offerName ?? ''],
						})}
					</Text>
					<CheckCircleFilledIcon
						color="icon.accent.primary.default"
						boxSize="40px"
					/>
					<Button as="a" href={getDeezerUrl('/')}>
						{t('common.launch_deezer')}
					</Button>
				</VersoCard>
			</FlippingCard>
			<GiftFloatingCards isSuccess={step === 'success'} pt="spacing.xl" />
		</InAppFormLayout>
	);
};

export const getStaticProps: DzrGetStaticProps = async ({
	locale = '',
	params,
}) => {
	const translations = await getStaticTranslations(locale, {
		appName: params?.appName,
		additionnalNamespaces: ['gift', 'error'],
	});
	const {appName, colorMode, template} = getTemplate('deezer');
	return {
		props: {
			appName,
			colorMode,
			template,
			...translations,
		},
	};
};

export default GiftPage;
